import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import type { ChangeEvent, SyntheticEvent } from "react";
import { Navigate, useNavigate, useParams, useLocation } from "react-router";
import { useMutation, useQuery } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import FavoriteIcon from "@mui/icons-material/Favorite";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import DashboardHeader from "../../components/dashboard-header";
import { useFirebaseImageUpload } from "../../components/firebase-image-upload/use-firebase-image-upload";
import { useUserContext } from "../../hooks/userContext";
import { GET_HOMEOWNER_MATCHING_PROFILE } from "../../graphql/homeowner-matching-profile";
import {
  CREATE_HOMEOWNER_BIO,
  GET_HOMEOWNER_BIO,
  UPDATE_HOMEOWNER_BIO,
} from "../../graphql/homeowner-bio";
import { GET_HOMEOWNER_PROJECTS } from "../../graphql/homeowner-project";
import { Pages } from "../../utils";
import { FirebasePaths } from "../../utils/firebase";
import type { DesignerMatchingProfile, HomeOwnerUser } from "../../__generated__/types";
import { CARD_BORDER, CARD_RADIUS, PROFILE_TABS } from "./constants";
import { PRIORITY_LABELS } from "./data";
import { formatPersonalityType, formatValue, toTitleCase } from "./utils";
import type {
  HomeownerBioQueryData,
  HomeownerBioQueryVars,
  HomeownerProfileQueryData,
  HomeownerProfileQueryVars,
  HomeownerProjectRecord,
  HomeownerProjectsQueryData,
  HomeownerProjectsQueryVars,
} from "./types";
import FloatingChatWidget from "../../components/floating-chat-widget";
import { useNotification } from "../../contexts/NotificationContext";
import { usePresence } from "../../contexts/PresenceContext";
import { useUserContextDispatch } from "../../hooks/userContext";
import HeaderSection from "./components/HeaderSection";
import PersonalityTab from "./tabs/PersonalityTab";
import DesignStyleTab from "./tabs/DesignStyleTab";
import ProjectTab from "./tabs/ProjectTab";
import PreferenceTab from "./tabs/PreferenceTab";
import NotesTab from "./tabs/NotesTab";
import "./styles.scss";
import {
  fetchMessagePermission,
  fetchMatchReaction,
  fetchMatches,
  fetchMatchProfile,
  fetchPresenceStatuses,
  postMatchReaction,
  type MatchProfileDetail,
  type MatchReaction,
} from "../../services/matchesApi";
import { fetchActiveCollaboration } from "../../services/collaborationApi";
import {
  MATCH_REACTION_EVENT,
  broadcastMatchReactionUpdate,
  getMatchReactionStorageKey,
  readMatchReactionOverrides,
  type MatchReactionOverrideMap,
} from "../../utils/match-reaction-cache";
import { useGetDesignerMatchingProfileQuery } from "../../hooks/use-get-designer-matching-profile";
import { formatDistance, getMatchCandidateIds } from "../matches-page/utils";

const normalizeReaction = (
  reaction: MatchReaction | null | undefined
): MatchReaction | null => {
  if (reaction === "love_like") return "love";
  return reaction ?? null;
};

const hasLoveReaction = (reaction: MatchReaction | null | undefined) =>
  normalizeReaction(reaction) === "love";

const hasLikeReaction = (reaction: MatchReaction | null | undefined) =>
  normalizeReaction(reaction) === "like";

const getNextReaction = (
  current: MatchReaction | null,
  next: MatchReaction
): MatchReaction => {
  const normalizedCurrent = normalizeReaction(current);
  if (next === null) return null;
  if (next === "not_for_me") {
    return normalizedCurrent === "not_for_me" ? null : "not_for_me";
  }
  if (next === "love") {
    return normalizedCurrent === "love" ? null : "love";
  }
  if (next === "like") {
    return normalizedCurrent === "like" ? null : "like";
  }
  if (next === "love_like") {
    return "love";
  }
  return normalizedCurrent ?? null;
};

const getReactionOverride = (
  candidates: Array<string | null | undefined>,
  overrides: MatchReactionOverrideMap
) => {
  for (const candidate of candidates) {
    if (!candidate) continue;
    if (Object.prototype.hasOwnProperty.call(overrides, candidate)) {
      return overrides[candidate] ?? null;
    }
  }
  return undefined;
};

const partnershipOptionLabels: Record<string, string> = {
  fullService: "Full Service",
  consultant: "Consultant",
  eDesign: "E-Design",
};

const formatEnumLabel = (value: string) => {
  const withSpaces = value.replace(/([a-z])([A-Z])/g, "$1 $2");
  return toTitleCase(withSpaces);
};

type HomeownerProfileRouteState = {
  topMatchTags?: string[];
  matchScore?: number | null;
  distanceMiles?: number | null;
  locationLabel?: string;
};

export const HomeownerProfilePage = () => {
  const { homeOwnerId } = useParams<{ homeOwnerId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useUserContext();
  const { showError, showSuccess } = useNotification();
  const { getPresenceStatus } = usePresence();
  const { updateHomeownerAccountData } = useUserContextDispatch();
  const { loading: profileImageLoading, onUpload: uploadProfileImage } =
    useFirebaseImageUpload(FirebasePaths.HOMEOWNER_PROFILE_IMAGE);
  const homeownerUser = user as Partial<HomeOwnerUser> | null;
  const effectiveHomeOwnerId = homeOwnerId ?? user?.id ?? "";
  const isMobile = useMediaQuery("(max-width: 900px)");
  const chatOpenRef = useRef<(() => void) | null>(null);
  const isDesignerViewer = (user?.accountType ?? "").toLowerCase() === "designer";
  const isHomeownerViewer = (user?.accountType ?? "").toLowerCase() === "homeowner";
  const canEditProfileImage =
    isHomeownerViewer && user?.id === effectiveHomeOwnerId;
  const { getDesignerMatchingProfile } = useGetDesignerMatchingProfileQuery();

  const [reaction, setReaction] = useState<MatchReaction | null>(null);
  const [contactPermission, setContactPermission] = useState<{
    allowed: boolean;
    reason: string | null;
  }>({ allowed: true, reason: null });
  const [homeownerPresenceFallback, setHomeownerPresenceFallback] = useState<
    boolean | null
  >(null);
  const [activeDesignerId, setActiveDesignerId] = useState<string | null>(null);
  const [activeDesignerProfile, setActiveDesignerProfile] =
    useState<DesignerMatchingProfile | null>(null);
  const [activeDesignerMatchProfile, setActiveDesignerMatchProfile] =
    useState<MatchProfileDetail | null>(null);
  const [activeDesignerMatchScore, setActiveDesignerMatchScore] = useState<number | null>(
    null
  );

  const realtimeHomeownerPresence = useMemo(() => {
    if (!effectiveHomeOwnerId) return undefined;
    return getPresenceStatus(effectiveHomeOwnerId, "Homeowner");
  }, [effectiveHomeOwnerId, getPresenceStatus]);

  const isHomeownerOnline =
    realtimeHomeownerPresence ?? homeownerPresenceFallback ?? false;

  useEffect(() => {
    if (!effectiveHomeOwnerId) {
      setHomeownerPresenceFallback(null);
      return;
    }

    if (realtimeHomeownerPresence !== undefined) {
      setHomeownerPresenceFallback(realtimeHomeownerPresence);
      return;
    }

    let cancelled = false;
    fetchPresenceStatuses([effectiveHomeOwnerId], "Homeowner")
      .then((presence) => {
        if (cancelled) return;
        const status = presence?.[effectiveHomeOwnerId];
        setHomeownerPresenceFallback(
          typeof status === "boolean" ? status : null
        );
      })
      .catch(() => {
        if (cancelled) return;
        setHomeownerPresenceFallback(null);
      });

    return () => {
      cancelled = true;
    };
  }, [effectiveHomeOwnerId, realtimeHomeownerPresence]);

  useEffect(() => {
    if (!isMobile) return;
    document.body.classList.add("homeowner-profile-mobile");
    return () => {
      document.body.classList.remove("homeowner-profile-mobile");
    };
  }, [isMobile]);

  useEffect(() => {
    if (!isDesignerViewer || !user?.id || !effectiveHomeOwnerId) return;
    let cancelled = false;
    const overrides = readMatchReactionOverrides(user?.id);
    const override = getReactionOverride(
      [effectiveHomeOwnerId, homeownerUser?._id, homeownerUser?.aliasId],
      overrides
    );
    if (override !== undefined) {
      setReaction(override);
      return;
    }
    fetchMatchReaction(user.id, effectiveHomeOwnerId)
      .then((storedReaction) => {
        if (!cancelled) {
          setReaction(storedReaction);
        }
      })
      .catch(() => {
        if (!cancelled) {
          showError("Unable to load reaction state.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveHomeOwnerId, homeownerUser?._id, homeownerUser?.aliasId, isDesignerViewer, showError, user?.id]);

  useEffect(() => {
    if (!effectiveHomeOwnerId) return;
    const handleReactionEvent = (event: Event) => {
      const detail = (event as CustomEvent<{
        profileId?: string;
        reaction?: MatchReaction;
      }>).detail;
      if (!detail?.profileId) return;
      const candidates = [
        effectiveHomeOwnerId,
        homeownerUser?._id as string | undefined,
        homeownerUser?.aliasId,
      ].filter(Boolean) as string[];
      if (!candidates.includes(detail.profileId)) return;
      setReaction(detail.reaction ?? null);
    };

    const handleStorage = (event: StorageEvent) => {
      const storageKey = getMatchReactionStorageKey(user?.id);
      if (!storageKey || event.key !== storageKey) return;
      const overrides = readMatchReactionOverrides(user?.id);
      const override = getReactionOverride(
        [effectiveHomeOwnerId, homeownerUser?._id, homeownerUser?.aliasId],
        overrides
      );
      if (override !== undefined) {
        setReaction(override);
      }
    };

    window.addEventListener(MATCH_REACTION_EVENT, handleReactionEvent as EventListener);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener(MATCH_REACTION_EVENT, handleReactionEvent as EventListener);
      window.removeEventListener("storage", handleStorage);
    };
  }, [effectiveHomeOwnerId, homeownerUser?._id, homeownerUser?.aliasId, user?.id]);

  useEffect(() => {
    if (!isDesignerViewer || !user?.id || !effectiveHomeOwnerId) {
      setContactPermission({ allowed: true, reason: null });
      return;
    }
    let cancelled = false;
    fetchMessagePermission(user.id, "Designer", effectiveHomeOwnerId, "Homeowner")
      .then((permission) => {
        if (cancelled) return;
        setContactPermission({
          allowed: permission?.allowed !== false,
          reason: permission?.reason ?? null,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setContactPermission({
          allowed: false,
          reason: "Unable to verify messaging permission right now. Please try again.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveHomeOwnerId, isDesignerViewer, reaction, user?.id]);

  useEffect(() => {
    if (!isHomeownerViewer || !user?.id) {
      setActiveDesignerId(null);
      return;
    }
    let cancelled = false;
    fetchActiveCollaboration(user.id, "homeowner")
      .then((response) => {
        if (cancelled) return;
        setActiveDesignerId(
          response?.collaboration?.designerId ?? response?.designer?._id ?? null
        );
      })
      .catch(() => {
        if (cancelled) return;
        setActiveDesignerId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isHomeownerViewer, user?.id]);

  const displayedDesignerId = useMemo(() => {
    if (isDesignerViewer) {
      return user?.id ?? null;
    }
    return activeDesignerId;
  }, [activeDesignerId, isDesignerViewer, user?.id]);

  useEffect(() => {
    if (!displayedDesignerId) {
      setActiveDesignerProfile(null);
      setActiveDesignerMatchProfile(null);
      setActiveDesignerMatchScore(null);
      return;
    }
    let cancelled = false;
    Promise.all([
      getDesignerMatchingProfile(displayedDesignerId, true).catch(() => null),
      fetchMatchProfile(displayedDesignerId).catch(() => null),
    ]).then(([designerProfile, matchProfile]) => {
      if (cancelled) return;
      setActiveDesignerProfile(designerProfile ?? null);
      setActiveDesignerMatchProfile(matchProfile ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [displayedDesignerId, getDesignerMatchingProfile]);

  useEffect(() => {
    if (!isHomeownerViewer || !activeDesignerId || !user?.id) {
      setActiveDesignerMatchScore(null);
      return;
    }
    let cancelled = false;
    const loadMatchScore = async () => {
      let page = 1;
      let foundScore: number | null = null;
      let hasNextPage = true;
      while (hasNextPage && !foundScore && page <= 6) {
        const data = await fetchMatches(user.id, page);
        const match = data.matches.find((candidate) =>
          getMatchCandidateIds(candidate as any).includes(activeDesignerId)
        );
        const score =
          match && typeof match.matchScore === "number" && !Number.isNaN(match.matchScore)
            ? match.matchScore
            : null;
        if (score !== null) {
          foundScore = score;
          break;
        }
        hasNextPage = Boolean(data.hasNextPage);
        page += 1;
      }
      if (!cancelled) {
        setActiveDesignerMatchScore(foundScore);
      }
    };
    loadMatchScore().catch(() => {
      if (!cancelled) {
        setActiveDesignerMatchScore(null);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [activeDesignerId, isHomeownerViewer, user?.id]);

  const { data: matchingData } = useQuery<
    HomeownerProfileQueryData,
    HomeownerProfileQueryVars
  >(GET_HOMEOWNER_MATCHING_PROFILE, {
    variables: { homeOwnerId: effectiveHomeOwnerId },
    skip: !effectiveHomeOwnerId,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

  const { data: bioData, refetch: refetchBio } = useQuery<
    HomeownerBioQueryData,
    HomeownerBioQueryVars
  >(GET_HOMEOWNER_BIO, {
    variables: { homeOwnerId: effectiveHomeOwnerId },
    skip: !effectiveHomeOwnerId,
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
  });
  const [createHomeOwnerBioMutation] = useMutation(CREATE_HOMEOWNER_BIO);
  const [updateHomeOwnerBioMutation] = useMutation(UPDATE_HOMEOWNER_BIO);

  const { data: projectsData } = useQuery<
    HomeownerProjectsQueryData,
    HomeownerProjectsQueryVars
  >(GET_HOMEOWNER_PROJECTS, {
    variables: { homeOwnerId: effectiveHomeOwnerId },
    skip: !effectiveHomeOwnerId,
  });

  const profile = matchingData?.getHomeOwnerMatchingProfile ?? null;
  const preferences = profile?.preferences;
  const bio = bioData?.getHomeOwnerBio ?? null;
  const [profilePreview, setProfilePreview] = useState<string | null>(null);
  const [isSavingProfileImage, setIsSavingProfileImage] = useState(false);

  const [profileTab, setProfileTab] = useState(0);
  const profileState = location.state as HomeownerProfileRouteState | null;

  const personalityType = useMemo(
    () =>
      formatValue(formatPersonalityType(profile?.personalityType), "Extrovert Blue Orange"),
    [profile?.personalityType]
  );

  const matchTagsFromState = useMemo(() => {
    const fromState = profileState?.topMatchTags;
    return Array.isArray(fromState)
      ? fromState.filter((tag) => typeof tag === "string" && tag.trim()).slice(0, 3)
      : [];
  }, [profileState?.topMatchTags]);

  const matchScoreFromState = useMemo(() => {
    const raw = profileState?.matchScore;
    return typeof raw === "number" && !Number.isNaN(raw) ? raw : null;
  }, [profileState?.matchScore]);
  const matchDistanceFromState = useMemo(() => {
    const raw = profileState?.distanceMiles;
    return typeof raw === "number" && !Number.isNaN(raw) ? raw : null;
  }, [profileState?.distanceMiles]);
  const matchLocationLabel = useMemo(() => {
    const raw = profileState?.locationLabel;
    return typeof raw === "string" && raw.trim().length > 0 ? raw.trim() : null;
  }, [profileState?.locationLabel]);
  const distanceLabel = useMemo(
    () =>
      matchDistanceFromState !== null ? formatDistance(matchDistanceFromState) : null,
    [matchDistanceFromState]
  );
  const hasMatchContext =
    matchScoreFromState !== null || matchTagsFromState.length > 0;

  const hasActiveDesigner = isHomeownerViewer && Boolean(activeDesignerId);
  const matchedDesignerTopTags = useMemo(() => {
    const tags = activeDesignerMatchProfile?.topMatchTags ?? [];
    return tags
      .filter((tag: unknown): tag is string => typeof tag === "string" && tag.trim().length > 0)
      .slice(0, 3);
  }, [activeDesignerMatchProfile?.topMatchTags]);

  const topMatchingAttributes = useMemo(() => {
    if (!hasMatchContext) return [];
    if (matchTagsFromState.length) return matchTagsFromState;

    const order: string[] = profile?.priorities?.order ?? [];
    const labels = order
      .map((key) => PRIORITY_LABELS[key] || toTitleCase(key))
      .filter(Boolean)
      .slice(0, 3);
    if (labels.length) return labels;
    return hasMatchContext ? ["Budget", "Style", "Color"] : [];
  }, [hasMatchContext, matchTagsFromState, profile?.priorities?.order]);
  const partnershipStyle = useMemo(
    () =>
      hasMatchContext
        ? formatValue(
            preferences?.partnershipStyle
              ? toTitleCase(preferences.partnershipStyle)
              : "",
            "Designer Full Service"
          )
        : "",
    [hasMatchContext, preferences?.partnershipStyle]
  );

  const topDesignerSkills = useMemo(() => {
    if (!hasMatchContext) return [];
    const skills: string[] = preferences?.skillRankings ?? [];
    const formatted = skills.map((skill) => toTitleCase(skill)).slice(0, 3);
    return formatted.length
      ? formatted
      : ["Budget Management", "Creativity", "Problem Solving"];
  }, [hasMatchContext, preferences?.skillRankings]);

  const matchedDesignerPartnershipStyle = useMemo(() => {
    const options = activeDesignerProfile?.workStyle?.partnershipOptions ?? [];
    const labels = (options as Array<string | null | undefined>)
      .filter((option): option is string => Boolean(option))
      .map((option) => partnershipOptionLabels[option] ?? formatEnumLabel(option));
    return labels.join(", ");
  }, [activeDesignerProfile?.workStyle?.partnershipOptions]);

  const matchedDesignerSkills = useMemo(() => {
    const strengths = activeDesignerProfile?.expertise?.competency?.strengths ?? [];
    const normalized = (strengths as Array<{ strength?: string | null; ranking?: number | null }>)
      .map((item) => ({
        strength: item?.strength ? String(item.strength) : "",
        ranking: typeof item?.ranking === "number" ? item.ranking : null,
      }))
      .filter((item) => item.strength);
    if (!normalized.length) return [];
    const sorted = [...normalized].sort((a, b) => {
      if (a.ranking === null && b.ranking === null) return 0;
      if (a.ranking === null) return 1;
      if (b.ranking === null) return -1;
      return a.ranking - b.ranking;
    });
    return sorted.slice(0, 3).map((item) => formatEnumLabel(item.strength));
  }, [activeDesignerProfile?.expertise?.competency?.strengths]);

  const matchedDesignerMatchScore = useMemo(() => {
    const rating = activeDesignerMatchProfile?.compatibilityRating;
    if (typeof rating !== "number" || Number.isNaN(rating)) return null;
    return Math.max(0, Math.min(100, rating * 20));
  }, [activeDesignerMatchProfile?.compatibilityRating]);

  const matchedDesignerPersonalityType =
    activeDesignerProfile?.personalityType ??
    activeDesignerMatchProfile?.personalityStyleResult ??
    null;

  const displayMatchScore = hasActiveDesigner
    ? activeDesignerMatchScore ?? matchedDesignerMatchScore
    : hasMatchContext
    ? matchScoreFromState
    : null;

  const displayTopMatchingAttributes = matchedDesignerTopTags.length
    ? matchedDesignerTopTags
    : hasActiveDesigner
    ? ["Budget", "Style", "Color"]
    : topMatchingAttributes;
  const displayPartnershipStyle = hasActiveDesigner
    ? matchedDesignerPartnershipStyle || "Designer Full Service"
    : partnershipStyle;
  const displayTopDesignerSkills = matchedDesignerSkills.length
    ? matchedDesignerSkills
    : hasActiveDesigner
    ? ["Budget Management", "Creativity", "Problem Solving"]
    : topDesignerSkills;

  const hireLikelihoodValue = preferences?.hireLikelihood ?? "";
  const leadReadiness = useMemo(() => {
    const normalized = hireLikelihoodValue.trim().toLowerCase();
    if (normalized.includes("absolute")) {
      return {
        label: "Hot Lead",
        tooltip: "It's an absolute, just need to find the right designer",
        backgroundColor: "#D43C00",
        textColor: "#FFFFFF",
      };
    }
    if (normalized.includes("warm opportunity") || normalized.includes("highly likely")) {
      return {
        label: "Warm Lead",
        tooltip: "Highly likely, with the right match",
        backgroundColor: "#FFD15D",
        textColor: "#8D6500",
      };
    }
    if (normalized.includes("not likely")) {
      return {
        label: "Early Stage",
        tooltip: "Not likely, just dreaming/researching options",
        backgroundColor: "#B5BBDC",
        textColor: "#3D4368",
      };
    }
    return {
      label: "Warm Lead",
      tooltip: "Highly likely, with the right match",
      backgroundColor: "#FFD15D",
      textColor: "#8D6500",
    };
  }, [hireLikelihoodValue]);

  const projects = useMemo(
    () => (projectsData?.getHomeOwnerProjects ?? []) as HomeownerProjectRecord[],
    [projectsData]
  );

  const fallbackMatchName = effectiveHomeOwnerId
    ? `Match_${effectiveHomeOwnerId.slice(-5)}`
    : "Homeowner";

  const homeownerDisplayName = useMemo(() => {
    if (user?.id === effectiveHomeOwnerId) {
      const fullName = [user?.firstName, user?.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
      return (
        fullName || user?.aliasId || fallbackMatchName
      );
    }
    return fallbackMatchName;
  }, [user, effectiveHomeOwnerId, fallbackMatchName]);

  const handleContactHomeowner = useCallback(() => {
    if (!contactPermission.allowed) {
      showError(
        contactPermission.reason || "Messaging is unavailable for this match."
      );
      return;
    }
    chatOpenRef.current?.();
  }, [contactPermission.allowed, contactPermission.reason, showError]);

  const handleReactionChange = useCallback(
    async (next: MatchReaction) => {
      if (!isDesignerViewer || !user?.id || !effectiveHomeOwnerId) return;
      const current = reaction ?? null;
      const updated = getNextReaction(current, next);
      setReaction(updated);
      try {
        await postMatchReaction(effectiveHomeOwnerId, updated, user.id);
        broadcastMatchReactionUpdate(effectiveHomeOwnerId, updated, user?.id);
      } catch {
        showError("Unable to save your reaction. Please try again.");
        setReaction(current);
      }
    },
    [effectiveHomeOwnerId, isDesignerViewer, reaction, showError, user?.id]
  );

  const isLoved = hasLoveReaction(reaction);
  const isLiked = hasLikeReaction(reaction);
  const isDismissed = reaction === "not_for_me";
  const isContactDisabled = isDesignerViewer && !contactPermission.allowed;

  useEffect(() => {
    if (isSavingProfileImage) return;
    const imageUrl = bio?.image?.url ?? null;
    setProfilePreview((prev) => {
      if (!prev || !prev.startsWith("blob:")) {
        return imageUrl;
      }
      return prev;
    });
  }, [bio?.image?.url, isSavingProfileImage]);

  useEffect(() => {
    if (!profilePreview || !profilePreview.startsWith("blob:")) {
      return;
    }
    return () => {
      URL.revokeObjectURL(profilePreview);
    };
  }, [profilePreview]);

  const handleProfileImageUpload = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      if (!canEditProfileImage || !effectiveHomeOwnerId) return;
      const files = event.target.files;
      const file = files?.[0];
      if (!file) return;
      const localUrl = URL.createObjectURL(file);
      setProfilePreview(localUrl);
      setIsSavingProfileImage(true);
      try {
        const uploaded = await uploadProfileImage(files);
        if (!uploaded?.length) {
          throw new Error("Unable to upload profile image.");
        }
        const uploadedImageId = String(uploaded[0]._id ?? "");
        const uploadedImageUrl = (uploaded[0].url as string) ?? "";
        if (bio?._id) {
          await updateHomeOwnerBioMutation({
            variables: {
              _id: bio._id,
              image: uploadedImageId || null,
            },
          });
        } else {
          await createHomeOwnerBioMutation({
            variables: {
              homeOwnerId: effectiveHomeOwnerId,
              image: uploadedImageId || null,
            },
          });
        }
        if (uploadedImageId) {
          await updateHomeownerAccountData({
            _id: effectiveHomeOwnerId,
            image: uploadedImageId,
          });
        }
        if (uploadedImageUrl) {
          setProfilePreview(uploadedImageUrl);
          if (typeof window !== "undefined") {
            localStorage.setItem("homeownerAvatarUrl", uploadedImageUrl);
            window.dispatchEvent(
              new CustomEvent("homeowner-avatar-updated", {
                detail: uploadedImageUrl,
              })
            );
          }
        }
        await refetchBio();
        showSuccess("Profile image updated.");
      } catch (error) {
        showError("Unable to update profile image. Please try again.");
        setProfilePreview(bio?.image?.url ?? null);
      } finally {
        setIsSavingProfileImage(false);
        event.target.value = "";
      }
    },
    [
      bio?._id,
      bio?.image?.url,
      canEditProfileImage,
      createHomeOwnerBioMutation,
      effectiveHomeOwnerId,
      refetchBio,
      showError,
      showSuccess,
      updateHomeOwnerBioMutation,
      updateHomeownerAccountData,
      uploadProfileImage,
    ]
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress sx={{ color: "#00A06D" }} />
      </Box>
    );
  }

  if (!effectiveHomeOwnerId) {
    return <Navigate to={Pages.DASHBOARD} replace />;
  }

  return (
    <Box className={isMobile ? "homeowner-profile-mobile-wrapper" : undefined}>
      {isMobile ? (
        <Box className="homeowner-profile-mobile-header">
          <Typography className="homeowner-profile-mobile-title">
            {homeownerDisplayName}
          </Typography>
          <IconButton
            aria-label="Close profile"
            onClick={() => navigate(-1)}
            className="homeowner-profile-mobile-close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      ) : (
        <DashboardHeader title="Homeowner Profile" />
      )}
      <Box
        className="homeowner-profile-content"
        sx={{ width: "100%", px: { xs: 0, md: 3 }, pb: { xs: 12, md: 6 } }}
      >
        <Paper
          elevation={0}
          className="homeowner-profile-card"
          sx={{
            borderRadius: CARD_RADIUS,
            border: CARD_BORDER,
            backgroundColor: "#fff",
            overflow: "visible",
            position: "relative",
          }}
        >
          <HeaderSection
            effectiveHomeOwnerId={effectiveHomeOwnerId}
            user={user}
            homeownerUser={homeownerUser}
            profile={profile}
            projects={projects}
            profileImageUrl={profilePreview ?? bio?.image?.url ?? null}
            personalityType={personalityType}
            topMatchingAttributes={displayTopMatchingAttributes}
            partnershipStyle={displayPartnershipStyle}
            topDesignerSkills={displayTopDesignerSkills}
            leadReadiness={leadReadiness}
            onContactHomeowner={handleContactHomeowner}
            isContactDisabled={isContactDisabled}
            contactDisabledReason={contactPermission.reason}
            isLoved={isLoved}
            isLiked={isLiked}
            isDismissed={isDismissed}
            showActions={isDesignerViewer}
            enableActions={isDesignerViewer}
            onLove={() => handleReactionChange("love")}
            onLike={() => handleReactionChange("like")}
            onDismiss={() => handleReactionChange("not_for_me")}
            canEditProfileImage={canEditProfileImage}
            onProfileImageUpload={handleProfileImageUpload}
            isProfileImageUploading={profileImageLoading || isSavingProfileImage}
            matchScore={displayMatchScore}
            distanceLabel={distanceLabel}
            matchLocationLabel={matchLocationLabel}
            isOnline={isHomeownerOnline}
          />

          <Box sx={{ pt: { xs: 1, md: 2 } }}>
            <Tabs
              value={profileTab}
              onChange={(_event: SyntheticEvent, value: number) =>
                setProfileTab(value)
              }
              variant="scrollable"
              TabIndicatorProps={{
                style: {
                  backgroundColor: "#6D63FF",
                  height: 3,
                  borderRadius: "3px 3px 0 0",
                },
              }}
              sx={{
                borderBottom: "1px solid #D7D3F0",
                mt: { xs: 1.5, md: 0 },
                mb: { xs: 1.5, md: 0 },
                px: { xs: 2, md: 0 },
                "& .MuiTabs-flexContainer": {
                  gap: { xs: 1, md: 0.5 },
                },
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#6B7280",
                  fontSize: "0.95rem",
                  minHeight: 44,
                  px: 2.5,
                },
                "& .MuiTab-root.Mui-selected": {
                  color: "#6D63FF",
                },
              }}
            >
              {PROFILE_TABS.map((tab) => (
                <Tab key={tab} label={tab} />
              ))}
            </Tabs>

            {profileTab === 0 ? (
              <PersonalityTab
                personalityType={personalityType}
                profilePersonalityType={profile?.personalityType}
                designerPersonalityType={
                  displayedDesignerId
                    ? (matchedDesignerPersonalityType as string | null)
                    : null
                }
                viewerRole={isDesignerViewer ? "designer" : "homeowner"}
                matchScore={displayMatchScore}
              />
            ) : profileTab === 1 ? (
              <DesignStyleTab profile={profile} />
            ) : profileTab === 2 ? (
              <ProjectTab
                profile={profile}
                preferences={preferences}
                projects={projects}
                homeOwnerId={effectiveHomeOwnerId}
              />
            ) : profileTab === 3 ? (
              <PreferenceTab projects={projects} />
            ) : profileTab === 4 ? (
              <NotesTab bio={bio} projects={projects} />
            ) : (
              <Box sx={{ py: 4 }}>
                <Typography color="text.secondary">Content coming soon.</Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
      {isMobile && isDesignerViewer && (
        <Box className="homeowner-profile-mobile-actions" role="group" aria-label="Match actions">
          <button
            type="button"
            className={`homeowner-profile-action-btn is-dismiss ${
              isDismissed ? "is-active" : ""
            }`}
            aria-label="Not for me"
            aria-pressed={isDismissed}
            onClick={() => handleReactionChange("not_for_me")}
            disabled={!isDesignerViewer}
          >
            <CloseIcon />
          </button>
          <button
            type="button"
            className={`homeowner-profile-action-btn is-like ${
              isLiked ? "is-active" : ""
            }`}
            aria-label="Like"
            aria-pressed={isLiked}
            onClick={() => handleReactionChange("like")}
            disabled={!isDesignerViewer}
          >
            <StarBorderIcon />
          </button>
          <button
            type="button"
            className={`homeowner-profile-action-btn is-love ${
              isLoved ? "is-active" : ""
            }`}
            aria-label="Love"
            aria-pressed={isLoved}
            onClick={() => handleReactionChange("love")}
            disabled={!isDesignerViewer}
          >
            <FavoriteIcon />
          </button>
          <FloatingChatWidget
            recipientId={effectiveHomeOwnerId}
            recipientRole="Homeowner"
            recipientName={homeownerDisplayName}
            recipientAvatarUrl={bio?.image?.url ?? null}
            onReady={(openChat) => {
              chatOpenRef.current = openChat;
            }}
            renderTrigger={(openChat) => (
              <button
                type="button"
                className="homeowner-profile-action-btn is-contact"
                aria-label="Contact"
                disabled={isContactDisabled}
                title={
                  isContactDisabled
                    ? contactPermission.reason ||
                      "Messaging is unavailable for this match."
                    : undefined
                }
                onClick={openChat}
              >
                <ChatBubbleOutlineIcon />
              </button>
            )}
            isDisabled={isContactDisabled}
            disabledReason={contactPermission.reason}
          />
        </Box>
      )}
      {!isMobile && isDesignerViewer && (
        <FloatingChatWidget
          recipientId={effectiveHomeOwnerId}
          recipientRole="Homeowner"
          recipientName={homeownerDisplayName}
          recipientAvatarUrl={bio?.image?.url ?? null}
          isDisabled={isContactDisabled}
          disabledReason={contactPermission.reason}
          onReady={(openChat) => {
            chatOpenRef.current = openChat;
          }}
        />
      )}
    </Box>
  );
};

export default HomeownerProfilePage;
